import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Moon, Sun, Globe, Bell, Lock, Database, Info } from 'lucide-react'
import { PageWrapper } from '@/components/animated/page-wrapper'
import { useUIStore } from '@/stores/ui'

export function Settings() {
  const { theme, setTheme } = useUIStore()

  return (
    <TooltipProvider>
      <PageWrapper>
        <div className="space-y-6 bg-gray-50 dark:bg-background min-h-screen -m-6 p-6">
          <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-light tracking-tight text-foreground">Einstellungen</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Verwalte deine App-Einstellungen und Präferenzen
            </p>
          </div>

          {/* Appearance Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                Darstellung
              </CardTitle>
              <CardDescription>
                Passe das Erscheinungsbild der Anwendung an
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Zwischen hellem und dunklem Modus wechseln
                  </p>
                </div>
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Sprache & Region
              </CardTitle>
              <CardDescription>
                Stelle deine bevorzugte Sprache ein
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>Sprache</Label>
                <Select defaultValue="de">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Zeitzone</Label>
                <Select defaultValue="europe/vienna">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="europe/vienna">Europa/Wien (CET)</SelectItem>
                    <SelectItem value="europe/berlin">Europa/Berlin (CET)</SelectItem>
                    <SelectItem value="europe/zurich">Europa/Zürich (CET)</SelectItem>
                    <SelectItem value="europe/paris">Europa/Paris (CET)</SelectItem>
                    <SelectItem value="europe/rome">Europa/Rom (CET)</SelectItem>
                    <SelectItem value="europe/madrid">Europa/Madrid (CET)</SelectItem>
                    <SelectItem value="europe/amsterdam">Europa/Amsterdam (CET)</SelectItem>
                    <SelectItem value="europe/brussels">Europa/Brüssel (CET)</SelectItem>
                    <SelectItem value="europe/copenhagen">Europa/Kopenhagen (CET)</SelectItem>
                    <SelectItem value="europe/stockholm">Europa/Stockholm (CET)</SelectItem>
                    <SelectItem value="europe/oslo">Europa/Oslo (CET)</SelectItem>
                    <SelectItem value="europe/prague">Europa/Prag (CET)</SelectItem>
                    <SelectItem value="europe/budapest">Europa/Budapest (CET)</SelectItem>
                    <SelectItem value="europe/warsaw">Europa/Warschau (CET)</SelectItem>
                    <SelectItem value="europe/london">Europa/London (GMT)</SelectItem>
                    <SelectItem value="europe/dublin">Europa/Dublin (GMT)</SelectItem>
                    <SelectItem value="europe/lisbon">Europa/Lissabon (WET)</SelectItem>
                    <SelectItem value="europe/athens">Europa/Athen (EET)</SelectItem>
                    <SelectItem value="europe/helsinki">Europa/Helsinki (EET)</SelectItem>
                    <SelectItem value="europe/istanbul">Europa/Istanbul (TRT)</SelectItem>
                    <SelectItem value="europe/moscow">Europa/Moskau (MSK)</SelectItem>
                    <SelectItem value="america/new_york">Amerika/New York (EST)</SelectItem>
                    <SelectItem value="america/chicago">Amerika/Chicago (CST)</SelectItem>
                    <SelectItem value="america/denver">Amerika/Denver (MST)</SelectItem>
                    <SelectItem value="america/los_angeles">Amerika/Los Angeles (PST)</SelectItem>
                    <SelectItem value="america/toronto">Amerika/Toronto (EST)</SelectItem>
                    <SelectItem value="america/mexico_city">Amerika/Mexiko-Stadt (CST)</SelectItem>
                    <SelectItem value="america/sao_paulo">Amerika/São Paulo (BRT)</SelectItem>
                    <SelectItem value="asia/tokyo">Asien/Tokio (JST)</SelectItem>
                    <SelectItem value="asia/shanghai">Asien/Shanghai (CST)</SelectItem>
                    <SelectItem value="asia/hong_kong">Asien/Hongkong (HKT)</SelectItem>
                    <SelectItem value="asia/singapore">Asien/Singapur (SGT)</SelectItem>
                    <SelectItem value="asia/dubai">Asien/Dubai (GST)</SelectItem>
                    <SelectItem value="asia/kolkata">Asien/Kalkutta (IST)</SelectItem>
                    <SelectItem value="australia/sydney">Australien/Sydney (AEDT)</SelectItem>
                    <SelectItem value="australia/melbourne">Australien/Melbourne (AEDT)</SelectItem>
                    <SelectItem value="pacific/auckland">Pazifik/Auckland (NZDT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Benachrichtigungen
              </CardTitle>
              <CardDescription>
                Verwalte deine Benachrichtigungseinstellungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>E-Mail Benachrichtigungen</Label>
                  <p className="text-sm text-muted-foreground">
                    Erhalte Updates per E-Mail
                  </p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>FAQ Genehmigungen</Label>
                  <p className="text-sm text-muted-foreground">
                    Benachrichtigung bei neuen FAQs zur Prüfung
                  </p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>System-Updates</Label>
                  <p className="text-sm text-muted-foreground">
                    Benachrichtigung über System-Updates
                  </p>
                </div>
                <Switch />
              </div>
            </CardContent>
          </Card>

          {/* Privacy & Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Datenschutz & Sicherheit
              </CardTitle>
              <CardDescription>
                Verwalte deine Datenschutz- und Sicherheitseinstellungen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <div>
                    <Label>Zwei-Faktor-Authentifizierung</Label>
                    <p className="text-sm text-muted-foreground">
                      Zusätzliche Sicherheit für dein Konto
                    </p>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Muss von Admin freigeschaltet werden</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Button variant="outline" size="sm" disabled>Einrichten</Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <div>
                    <Label>Sitzungsverlauf</Label>
                    <p className="text-sm text-muted-foreground">
                      Aktive Sitzungen anzeigen und verwalten
                    </p>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Funktion derzeit nicht verfügbar</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Button variant="outline" size="sm" disabled>
                  Anzeigen
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Data Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Datenverwaltung
              </CardTitle>
              <CardDescription>
                Exportiere oder lösche deine Daten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Daten exportieren</Label>
                  <p className="text-sm text-muted-foreground">
                    Exportiere alle deine Daten als JSON
                  </p>
                </div>
                <Button variant="outline" size="sm">Exportieren</Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <div>
                    <Label>Konto löschen</Label>
                    <p className="text-sm text-muted-foreground">
                      Lösche dein Konto und alle zugehörigen Daten
                    </p>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Muss von Admin durchgeführt werden</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Button variant="destructive" size="sm" disabled>Löschen</Button>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </PageWrapper>
    </TooltipProvider>
  )
}
